import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';

// ── Types ──────────────────────────────────────────────────────────────────

interface SchoolProfileData {
  school_name: string;
  school_email: string;
  school_phone: string;
  school_address: string;
  school_website: string;
}

interface LogoFile {
  file_name: string;
  key: string;
  cdn_url: string;
  dev_url: string;
  mime_type: string;
  size: number;
  type: string;
}

interface OptionApiResponse {
  status: string;
  message?: string;
  data?: any;
  option?: {
    option_id: number;
    option_name: string;
    option_value: any;
  };
}

interface LogoApiResponse {
  status: string;
  message?: string;
  option_name?: string;
  option_id?: number;
  files?: LogoFile[];
}

// ── Constants ─────────────────────────────────────────────────────────────

const MAX_LOGO_SIZE_BYTES = 1.5 * 1024 * 1024; // 1.5 MB
const ACCEPTED_LOGO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];

/** Unwrap whatever the API returns into a plain string. Handles:
 *  - plain string (e.g. school_name stored as raw string, not serialized)
 *  - PHP serialized array deserialized to { 0: "val" } or { "0": "val" }
 *  - single-element JS array ["val"]
 *  - any other scalar
 *
 *  PHP's unserialize on a plain string returns the string itself via the
 *  API layer, so `data` may just be the raw string — always check that first.
 */
const extractOptionString = (raw: any): string => {
  if (raw === null || raw === undefined) return '';
  // Plain string — covers school_name and any non-serialized value
  if (typeof raw === 'string') return raw;
  // JS array from a PHP indexed array: ["val"]
  if (Array.isArray(raw)) return String(raw[0] ?? '');
  // Object from a PHP associative/indexed array: { "0": "val" }
  if (typeof raw === 'object') return String(raw[0] ?? raw['0'] ?? '');
  return String(raw);
};

const FIELD_OPTIONS: Array<{
  key: keyof SchoolProfileData;
  label: string;
  icon: string;
  placeholder: string;
  type?: string;
}> = [
  { key: 'school_name',    label: 'School Name',    icon: 'pi pi-building',  placeholder: 'Enter school name'    },
  { key: 'school_email',   label: 'Email Address',  icon: 'pi pi-envelope',  placeholder: 'Enter email address', type: 'email' },
  { key: 'school_phone',   label: 'Phone / Contact',icon: 'pi pi-phone',     placeholder: 'Enter phone number',  type: 'tel'   },
  { key: 'school_address', label: 'School Address', icon: 'pi pi-map-marker',placeholder: 'Enter school address' },
  { key: 'school_website', label: 'Website URL',    icon: 'pi pi-globe',     placeholder: 'https://yourschool.edu', type: 'url' },
];

// ── Component ──────────────────────────────────────────────────────────────

const SchoolProfileSettings: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [loading, setLoading]   = useState<boolean>(true);
  const [saving, setSaving]     = useState<boolean>(false);

  const [profile, setProfile] = useState<SchoolProfileData>({
    school_name:    '',
    school_email:   '',
    school_phone:   '',
    school_address: '',
    school_website: '',
  });

  // Logo state
  const [existingLogo, setExistingLogo]       = useState<LogoFile | null>(null);
  const [newLogoFile, setNewLogoFile]         = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl]   = useState<string | null>(null);
  const [removeLogo, setRemoveLogo]           = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Fetch on mount ──────────────────────────────────────────────────────

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async (): Promise<void> => {
    setLoading(true);
    try {
      await Promise.all([fetchProfileOptions(), fetchLogo()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfileOptions = async (): Promise<void> => {
    const keys: (keyof SchoolProfileData)[] = [
      'school_name', 'school_email', 'school_phone', 'school_address', 'school_website',
    ];

    // Fetch all options in parallel
    const results = await Promise.allSettled(
      keys.map((k) => apiClient.get<OptionApiResponse>(`/options/${k}`))
    );

    const updated: Partial<SchoolProfileData> = {};
    results.forEach((result, i) => {
      if (result.status === 'fulfilled') {
        const res = result.value.data;
        if (res?.status === 'success') {
          const raw = res.data ?? res.option?.option_value;
          updated[keys[i]] = extractOptionString(raw);
        }
      }
    });

    setProfile((prev) => ({ ...prev, ...updated }));
  };

  const fetchLogo = async (): Promise<void> => {
    try {
      const res = await apiClient.get<LogoApiResponse>('/options-files/school_logo');
      if (res.data?.status === 'success' && res.data.files?.length) {
        setExistingLogo(res.data.files[0]);
      }
    } catch {
      // No logo yet — that's fine
    }
  };

  // ── Logo handlers ───────────────────────────────────────────────────────

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Invalid File',
        detail: 'Please upload a JPG, PNG, WebP, or SVG image.',
        life: 4000,
      });
      return;
    }

    if (file.size > MAX_LOGO_SIZE_BYTES) {
      toast.current?.show({
        severity: 'warn',
        summary: 'File Too Large',
        detail: 'Logo must be under 1.5 MB.',
        life: 4000,
      });
      return;
    }

    setNewLogoFile(file);
    setRemoveLogo(false);
    setLogoPreviewUrl(URL.createObjectURL(file));

    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveLogo = (): void => {
    setNewLogoFile(null);
    setLogoPreviewUrl(null);
    setRemoveLogo(true);
  };

  const handleCancelLogoChange = (): void => {
    setNewLogoFile(null);
    setLogoPreviewUrl(null);
    setRemoveLogo(false);
  };

  // ── Save ────────────────────────────────────────────────────────────────

  const handleSave = async (): Promise<void> => {
    if (!profile.school_name.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Required',
        detail: 'School name is required.',
        life: 4000,
      });
      return;
    }

    setSaving(true);
    try {
      await Promise.all([saveProfileOptions(), saveLogo()]);

      toast.current?.show({
        severity: 'success',
        summary: 'Saved',
        detail: 'School profile updated successfully.',
        life: 4000,
      });

      // Refresh logo state after save
      await fetchLogo();
      setNewLogoFile(null);
      setLogoPreviewUrl(null);
      setRemoveLogo(false);
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.message || 'Failed to save school profile.',
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const saveProfileOptions = async (): Promise<void> => {
    const keys: (keyof SchoolProfileData)[] = [
      'school_name', 'school_email', 'school_phone', 'school_address', 'school_website',
    ];

    const filled = keys.filter((k) => profile[k].trim() !== '');
    if (filled.length === 0) return;

    await Promise.all(
      filled.map((k) =>
        apiClient.put(`/v2/options/${k}`, { value: [profile[k].trim()] })
      )
    );
  };

  const saveLogo = async (): Promise<void> => {
    // Case 1: New logo to upload
    if (newLogoFile) {
      const formData = new FormData();
      formData.append('files', newLogoFile);

      // If existing logo, send existing_files='none' to drop old before uploading new
      if (existingLogo) {
        formData.append('existing_files', 'none');
      }

      await apiClient.put('/options-files/school_logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return;
    }

    // Case 2: User explicitly removed the logo
    if (removeLogo && existingLogo) {
      await apiClient.delete(
        `/options-files/school_logo?file_key=${encodeURIComponent(existingLogo.key)}`
      );
      setExistingLogo(null);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────

  const currentLogoUrl = logoPreviewUrl ?? existingLogo?.cdn_url ?? null;
  const showLogoPreview = currentLogoUrl && !removeLogo;

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // ── Loading state ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '60px 24px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.12)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }}
        />
        <p style={{ color: '#64748b', fontSize: '14px' }}>Loading school profile...</p>
        <style>{`@keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.12)',
        }}
      >
        {/* ── Card Header ── */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            paddingBottom: '16px',
            borderBottom: '2px solid #f3f4f6',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i className="pi pi-image" style={{ fontSize: '18px', color: 'white' }} />
            </div>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e40af', margin: 0 }}>
                School Profile
              </h2>
              <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0 0' }}>
                Manage your school's identity and contact information
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <GlobalButton
              label=""
              onClick={fetchAll}
              disabled={loading || saving}
              icon="pi pi-refresh"
              variant="outline"
              size="small"
            />
            <GlobalButton
              label={saving ? 'Saving...' : 'Save Profile'}
              onClick={handleSave}
              disabled={saving}
              loading={saving}
              icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
              variant="success"
              size="small"
            />
          </div>
        </div>

        {/* ── Body: two columns on wide screens ── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) 169px',
            gap: '32px',
            alignItems: 'start',
          }}
        >
          {/* ── Left: text fields ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Row 1: School Name + Website */}
            <div style={{ display: 'flex', gap: '12px' }}>
              {(['school_name', 'school_website'] as const).map((key) => {
                const f = FIELD_OPTIONS.find((x) => x.key === key)!;
                return (
                  <div key={key} style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                      <i className={f.icon} style={{ marginRight: '6px', color: '#3b82f6' }} />
                      {f.label}
                      {key === 'school_name' && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
                    </label>
                    <input
                      type={f.type ?? 'text'}
                      value={profile[key]}
                      onChange={(e) => setProfile((prev) => ({ ...prev, [key]: e.target.value }))}
                      placeholder={f.placeholder}
                      disabled={saving}
                      style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', color: '#1f2937', background: saving ? '#f9fafb' : 'white', transition: 'border-color 0.15s', boxSizing: 'border-box' }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = '#3b82f6')}
                      onBlur={(e)  => (e.currentTarget.style.borderColor = '#e5e7eb')}
                    />
                  </div>
                );
              })}
            </div>

            {/* Row 2: Email + Phone */}
            <div style={{ display: 'flex', gap: '12px' }}>
              {(['school_email', 'school_phone'] as const).map((key) => {
                const f = FIELD_OPTIONS.find((x) => x.key === key)!;
                return (
                  <div key={key} style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                      <i className={f.icon} style={{ marginRight: '6px', color: '#3b82f6' }} />
                      {f.label}
                    </label>
                    <input
                      type={f.type ?? 'text'}
                      value={profile[key]}
                      onChange={(e) => setProfile((prev) => ({ ...prev, [key]: e.target.value }))}
                      placeholder={f.placeholder}
                      disabled={saving}
                      style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', color: '#1f2937', background: saving ? '#f9fafb' : 'white', transition: 'border-color 0.15s', boxSizing: 'border-box' }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = '#3b82f6')}
                      onBlur={(e)  => (e.currentTarget.style.borderColor = '#e5e7eb')}
                    />
                  </div>
                );
              })}
            </div>

            {/* Row 3: Address (full width) */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                <i className="pi pi-map-marker" style={{ marginRight: '6px', color: '#3b82f6' }} />
                School Address
              </label>
              <GlobalTextArea
                value={profile.school_address}
                onChange={(e) => setProfile((prev) => ({ ...prev, school_address: e.target.value }))}
                placeholder="Enter school address"
                disabled={saving}
                rows={3}
                autoResize={false}
                fullWidth
              />
            </div>

          </div>

          {/* ── Right: logo panel ── */}
          <div>
            <p
              style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '10px',
              }}
            >
              <i className="pi pi-image" style={{ marginRight: '6px', color: '#3b82f6' }} />
              School Logo
            </p>

            {/* Preview box */}
            <div
              style={{
                width: '100%',
                aspectRatio: '1',
                borderRadius: '12px',
                border: showLogoPreview
                  ? '2px solid rgba(59,130,246,0.3)'
                  : '2px dashed #d1d5db',
                background: showLogoPreview ? '#f8faff' : '#fafafa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                marginBottom: '12px',
                position: 'relative',
              }}
            >
              {showLogoPreview ? (
                <img
                  src={currentLogoUrl!}
                  alt="School logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    padding: '16px',
                    boxSizing: 'border-box',
                  }}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <i
                    className="pi pi-image"
                    style={{ fontSize: '36px', color: '#d1d5db', display: 'block', marginBottom: '8px' }}
                  />
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                    {removeLogo ? 'Logo will be removed on save' : 'No logo uploaded'}
                  </span>
                </div>
              )}

              {/* New file badge */}
              {newLogoFile && (
                <div
                  style={{
                    position: 'absolute',
                    top: '8px',
                    left: '8px',
                    background: '#1e40af',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: '700',
                    padding: '2px 7px',
                    borderRadius: '20px',
                    letterSpacing: '0.5px',
                  }}
                >
                  NEW
                </div>
              )}
            </div>

            {/* File info */}
            {newLogoFile && (
              <div
                style={{
                  background: '#f0f7ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '8px',
                  padding: '8px 10px',
                  marginBottom: '10px',
                  fontSize: '12px',
                  color: '#1e40af',
                }}
              >
                <div style={{ fontWeight: '600', marginBottom: '2px', wordBreak: 'break-all' }}>
                  {newLogoFile.name}
                </div>
                <div style={{ color: '#64748b' }}>{formatFileSize(newLogoFile.size)}</div>
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_LOGO_TYPES.join(',')}
                style={{ display: 'none' }}
                onChange={handleLogoSelect}
              />

              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={saving}
                style={{
                  width: '100%',
                  padding: '9px',
                  borderRadius: '8px',
                  border: '1.5px solid #3b82f6',
                  background: 'white',
                  color: '#1e40af',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => { if (!saving) e.currentTarget.style.background = '#eff6ff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
              >
                <i className="pi pi-upload" style={{ fontSize: '13px' }} />
                {existingLogo || newLogoFile ? 'Change Logo' : 'Upload Logo'}
              </button>

              {/* Cancel pending new logo */}
              {newLogoFile && (
                <button
                  onClick={handleCancelLogoChange}
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '9px',
                    borderRadius: '8px',
                    border: '1.5px solid #e5e7eb',
                    background: 'white',
                    color: '#6b7280',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  <i className="pi pi-times" style={{ fontSize: '13px' }} />
                  Cancel Change
                </button>
              )}

              {/* Remove existing logo */}
              {(existingLogo || newLogoFile) && !removeLogo && (
                <button
                  onClick={handleRemoveLogo}
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '9px',
                    borderRadius: '8px',
                    border: '1.5px solid #fecaca',
                    background: 'white',
                    color: '#dc2626',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                  onMouseEnter={(e) => { if (!saving) e.currentTarget.style.background = '#fff5f5'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
                >
                  <i className="pi pi-trash" style={{ fontSize: '13px' }} />
                  Remove Logo
                </button>
              )}

              {/* Undo remove */}
              {removeLogo && (
                <button
                  onClick={() => setRemoveLogo(false)}
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '9px',
                    borderRadius: '8px',
                    border: '1.5px solid #fde68a',
                    background: '#fffbeb',
                    color: '#92400e',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  <i className="pi pi-undo" style={{ fontSize: '13px' }} />
                  Undo Remove
                </button>
              )}
            </div>

            {/* Constraints hint */}
            <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '10px', lineHeight: 1.5 }}>
              JPG, PNG, WebP or SVG · Max 1.5 MB
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SchoolProfileSettings;